// Office Renewals page. Lists every active contract due to renew within 90 days
// with its current annual value and the proposed new value after escalation (CPI +
// the locked operational uplift), a per-row "Customer link" state for mechanism A (the interactive
// renewal link, Session AQW/M-1), and a per-row Draft renewal letter action (AI-drafted,
// editable, saved to Gmail drafts for review -- never sent automatically). Confirm
// this cycle's CPI on the Settings page to turn an indicative figure into a firm one.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { formatDate } from "../../lib/format";
import type { RenewalListItem, RenewalCpiStatus, RenewalProposal, RenewalCycleFinding, RenewalCycleState, XeroBillingActionRow } from "../../lib/api";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Button, buttonClasses } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { RenewalModal } from "./ContractsPage";
import { useSortableColumn, sortRows, type SortAccessors } from "../../lib/useSortableColumn";
import { SortableHeader } from "../../components/SortableHeader";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";

const TIER_LABEL: Record<string, string> = { core: "Core", care: "Care", complete: "Complete" };

// D7 (Session AIC): what happened to a proposal the office has already dealt with.
const PROPOSAL_STATUS_LABEL: Record<string, string> = {
  confirmed: "confirmed, price applied and letter drafted",
  skipped: "removed from the batch",
  failed: "needs attention",
};

// D6 (Session AIC). Short enough for a right-aligned money column; the full sentence is on the
// contract. "No price review" is a correct, settled state. The other two are the office's cue that
// something needs setting before this contract can ever be proposed for a rise.
const NO_ESCALATOR_LABEL: Record<NonNullable<RenewalListItem["noEscalatorReason"]>, string> = {
  intermediary: "No price review",
  rate_not_recorded: "Escalator not recorded",
  platform_not_configured: "Escalator not set up",
};

// Session AQW (M-1): mechanism A's (the interactive customer renewal link) state, in plain words, for
// the "Customer link" column. Computed server-side (contract.routes.ts loadRenewalCustomerLinkStates)
// from the SAME renewalLinkSpent / renewalRestartRefusal rules RenewalLinkPanel's own read already
// applies to one contract; this file only supplies the words.
type CustomerLinkState = NonNullable<RenewalListItem["customerLink"]>["state"];
const CUSTOMER_LINK_LABEL: Record<CustomerLinkState, (at: string | null) => string> = {
  not_issued: () => "Not issued",
  issued: (at) => (at ? `Issued ${formatDate(at)}` : "Issued"),
  opened: (at) => (at ? `Opened ${formatDate(at)}` : "Opened"),
  used: (at) => (at ? `Used ${formatDate(at)}` : "Used"),
  signed: () => "Signed",
  restartable: () => "Can be started again",
};

// Shared by the desktop cell and the mobile card's details line, so the two can never say something
// different about the same contract. "Can be started again" is the one state the office can act on
// from here, so it alone links (to the Renewal tab -- see M-1); the restart control itself stays on
// RenewalLinkPanel's own form, with its own reason field and two-step confirm (never a second writer).
function CustomerLinkText({ link, contractId }: { link: RenewalListItem["customerLink"]; contractId: string }) {
  const state = link?.state ?? "not_issued";
  const text = CUSTOMER_LINK_LABEL[state](link?.at ?? null);
  return state === "restartable" ? (
    <Link
      to={`/office/contracts/${contractId}?tab=renewal`}
      className="font-menu text-xs uppercase tracking-wide text-amber-400 hover:underline"
    >
      {text}
    </Link>
  ) : (
    <span className="text-xs text-ele-body">{text}</span>
  );
}

// Column sort accessors (Session AU, Item 3). Renewal sorts by the actual date; Current/Proposed
// numerically (nulls last). Client-side (the list is unpaginated).
const RENEWAL_SORT: SortAccessors<RenewalListItem> = {
  customer: (r) => r.customerName,
  site: (r) => r.siteName,
  tier: (r) => TIER_LABEL[r.tier] ?? r.tier,
  renewal: (r) => r.renewalDate,
  current: (r) => r.currentAnnual,
  proposed: (r) => r.proposedAnnual,
};

function gbp(n: number): string {
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}

export function RenewalsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<RenewalListItem[]>([]);
  const [cpi, setCpi] = useState<RenewalCpiStatus | null>(null);
  // Session AMW: the renewal-cycle findings over the whole active book (see RenewalCyclesPanel).
  const [cycles, setCycles] = useState<RenewalCycleFinding[]>([]);
  // Session ARY, Item 4: the Xero-billing action rows (see XeroBillingOutOfStepPanel).
  const [xeroOutOfStep, setXeroOutOfStep] = useState<XeroBillingActionRow[]>([]);
  // "ok", or the sentence saying the check could not run (review finding: an empty list read as all in step).
  const [xeroCheck, setXeroCheck] = useState<string>("ok");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [renewFor, setRenewFor] = useState<RenewalListItem | null>(null);
  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism).
  // jsdom has no matchMedia, so every existing test still renders the table it pins.
  const [mobileCards] = useState(initialMobileLayout);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    api.contracts
      .renewals()
      .then((r) => {
        if (!live) return;
        setRows(r.items);
        setCpi(r.cpi);
        setCycles(r.cycles ?? []);
        setXeroOutOfStep(r.xeroTemplateOutOfStep ?? []);
        setXeroCheck(r.xeroTemplateOutOfStepCheck ?? "ok");
      })
      .catch(() => live && setError(true))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, []);

  const sort = useSortableColumn("renewals");
  const sorted = useMemo(() => sortRows(rows, RENEWAL_SORT, sort), [rows, sort.sortKey, sort.sortDir]);

  return (
    <div>
      <PageHeader
        className="mb-4"
        title="Renewals"
        subtitle="Contracts renewing within the next 90 days, with the proposed new price after escalation."
      />

      {/*
        D13 (Session AIC). Three distinct states, deliberately not collapsed into one:
        NO figure at all is a hard stop, because nothing on this page can be priced and the old
        behaviour was to quietly price every renewal as though inflation were zero. A STALE figure
        is usable with a caveat. A GOOD figure is stated with its provenance, so the office can see
        which ONS reading a rise was calculated from without opening Settings.
      */}
      {cpi && cpi.rate == null ? (
        <p role="alert" className="mb-4 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-xs text-ele-danger">
          {cpi.message ?? "No CPI figure is available, so renewal prices cannot be calculated."}
        </p>
      ) : cpi && cpi.stale ? (
        // The house amber pattern (ConfirmDeleteModal): there is no `ele-amber` Tailwind token.
        <p role="alert" className="mb-4 rounded-ele border border-amber-500 bg-amber-500/10 px-3 py-2 text-xs text-[var(--ele-amber-text-soft)]">
          {cpi.message}
        </p>
      ) : cpi?.label ? (
        <p className="mb-4 rounded-ele border border-ele-blue bg-ele-surface px-3 py-2 text-xs text-ele-blue">
          Priced using {cpi.label}.
        </p>
      ) : null}

      {/*
        The legacy "indicative until this cycle's CPI is confirmed" banner was REMOVED here (Session
        AIC). It described the retired model, in which an unconfirmed CPI silently priced every
        renewal at zero inflation and the remedy was to type a figure by hand. `indicative` now means
        only that the ONS reading is stale, so that banner fired at the same time as the amber one
        above and said something different about the same state. Worse, it instructed the office to
        set a manual CPI precisely when ONS was lagging, and a manual figure OUTRANKS the live
        reading, so following it would have quietly pinned every future renewal to a hand-typed
        number.
      */}

      {!loading && !error && cycles.length > 0 && <RenewalCyclesPanel cycles={cycles} />}

      {/* Session ARY, Item 4: the Xero-billing companion panel, next to the renewal-cycles one --
          both are "things nobody has looked at" lists, checked live and never from a stored flag. */}
      {!loading && !error && xeroCheck !== "ok" && (
        <p role="alert" className="mb-4 rounded-ele border border-amber-500 bg-amber-500/10 px-3 py-2 text-xs text-[var(--ele-amber-text-soft)]">
          {xeroCheck} Contracts whose Xero billing is out of step are not listed until it runs.
        </p>
      )}
      {!loading && !error && xeroOutOfStep.length > 0 && <XeroBillingOutOfStepPanel rows={xeroOutOfStep} />}

      {/* Session BBJ (one renewal funnel): the customer renewal link is the default way to renew, so every row below
          opens the contract's Renewal tab with "Start renewal". The plain price rise letter stays, labelled as the
          secondary option. Nothing here starts a renewal by itself: the tab confirms the lifts first and applies
          the link, restart and live-successor rules. */}
      {!loading && !error && rows.length > 0 && (
        <p className="mb-4 rounded-ele border border-ele-hairline bg-ele-surface px-3 py-2 text-xs text-ele-body">
          <strong className="text-ele-strong">Start renewal</strong> is the usual way: it confirms the lifts, then issues the customer&apos;s own renewal link
          so they choose their cover and sign online. <strong className="text-ele-strong">Draft renewal letter</strong> is the secondary option: a plain
          price rise letter with no customer link.
        </p>
      )}

      <RenewalProposalsPanel />


      {loading ? (
        <Loader label="Loading the renewals..." />
      ) : error ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the renewals. Please try again in a moment.
        </p>
      ) : rows.length === 0 ? (
        <EmptyState message="No contracts are renewing within the next 90 days." />
      ) : mobileCards ? (
        /* At 375px the three surviving columns (Customer, Renewal date, Proposed) divided the
           strip so tightly that Proposed's longer fallback strings ("Escalator not recorded")
           truncated to nothing useful, and the customer name -- the thing this list is scanned
           by -- clipped too. The card stacks instead: the customer name never truncates, Site,
           Tier and Current (all DESKTOP-ONLY columns before this) come back, and Proposed's full
           value or fallback text reads in full in the right rail. */
        <MobileCardList label="Renewals">
          {sorted.map((r) => (
            <MobileListCard
              key={r.id}
              to={`/office/contracts/${r.id}?tab=renewal`}
              primary={r.customerName}
              secondary={r.siteName}
              meta={r.daysOverdue ? `Renewal ${formatDate(r.renewalDate)}, overdue by ${r.daysOverdue} day${r.daysOverdue === 1 ? "" : "s"}` : `Renews ${formatDate(r.renewalDate)}`}
              right={
                r.proposedAnnual == null ? (
                  /* Same D6 (Session AIC) fallback text as the desktop cell, just given room to
                     wrap instead of clipping: "No price review" is a settled state, "Escalator
                     not recorded" / "Escalator not set up" mean the contract needs attention. */
                  <span className="text-xs text-ele-body">
                    {r.noEscalatorReason ? NO_ESCALATOR_LABEL[r.noEscalatorReason] : "No CPI figure"}
                  </span>
                ) : (
                  <>
                    <span className="font-semibold text-ele-strong">{gbp(r.proposedAnnual)}</span>
                    {r.indicative && <span className="text-[10px] uppercase text-ele-blue">(indicative)</span>}
                  </>
                )
              }
              details={
                <>
                  <p className="text-xs text-ele-body">
                    {TIER_LABEL[r.tier] ?? r.tier} tier · Current {gbp(r.currentAnnual)}
                  </p>
                  {/* Session AQW (M-1): the same "Customer link" state the desktop table shows in its
                      own column, as a meta-line here since the card has no room for another column. */}
                  <p className="mt-0.5">
                    <CustomerLinkText link={r.customerLink} contractId={r.id} />
                  </p>
                </>
              }
              actions={
                <>
                  <Link to={`/office/contracts/${r.id}?tab=renewal`} className={buttonClasses("primary", "sm", "min-h-11 flex-1")}>
                    Start renewal
                  </Link>
                  <Button variant="ghost" size="sm" className="min-h-11 flex-1" onClick={() => setRenewFor(r)} title="A plain price rise letter with no customer link">
                    Draft renewal letter
                  </Button>
                </>
              }
            />
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "20%" }} />
              <col className="max-md:hidden" style={{ width: "15%" }} />
              <col className="max-md:hidden" style={{ width: "8%" }} />
              <col style={{ width: "12%" }} />
              <col className="max-md:hidden" style={{ width: "9%" }} />
              <col style={{ width: "12%" }} />
              {/* Session AQW (M-1): the "Customer link" column -- desktop-only, like Site/Tier/Current,
                  since the mobile card shows the same state as a meta-line instead (see `details` above). */}
              <col className="max-md:hidden" style={{ width: "13%" }} />
              <col className="max-md:hidden" style={{ width: "11%" }} />
              {/* Mobile-only chevron disclosure column (Session ACW): the row itself now navigates
                  to the contract detail page, which carries the same Draft renewal letter action. */}
              <col className="w-8 md:hidden" />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Customer" colKey="customer" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Site" colKey="site" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Tier" colKey="tier" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Renewal" colKey="renewal" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Current" colKey="current" sort={sort} onToggle={sort.toggle} align="right" className="max-md:hidden" />
                <SortableHeader label="Proposed" colKey="proposed" sort={sort} onToggle={sort.toggle} align="right" className="cell-actions" />
                {/* Not sortable: a plain-words state (M-10's search/sort rollout is separate scope). */}
                <th className="px-3 py-2 text-left max-md:hidden">Customer link</th>
                <th className="cell-actions px-3 py-2 text-right max-md:hidden">Renew</th>
                <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr
                  key={r.id}
                  className="group cursor-pointer border-t border-ele-purple-accent transition hover:bg-ele-row-hover"
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest("a,button,input,select,textarea,label")) return;
                    navigate(`/office/contracts/${r.id}?tab=renewal`);
                  }}
                >
                  <td className="px-3 py-2 align-top text-ele-strong">{r.customerName}</td>
                  <td className="px-3 py-2 align-top max-md:hidden">{r.siteName}</td>
                  <td className="px-3 py-2 align-top max-md:hidden">{TIER_LABEL[r.tier] ?? r.tier}</td>
                  <td className="px-3 py-2 align-top whitespace-nowrap">
                    {formatDate(r.renewalDate)}
                    {/* Session AMW: an elapsed anchor stays on this list until something settles it;
                        the office must be able to see at a glance which rows are already past. */}
                    {r.daysOverdue ? <span className="ml-1 text-[10px] uppercase text-ele-danger">overdue {r.daysOverdue}d</span> : null}
                  </td>
                  <td className="px-3 py-2 text-right align-top text-ele-strong max-md:hidden">{gbp(r.currentAnnual)}</td>
                  <td className="cell-actions px-3 py-2 text-right align-top text-ele-strong">
                    {r.proposedAnnual == null ? (
                      /*
                        D6 (Session AIC): a bare dash here was the silent state the decision is
                        about. "No price review" is correct and needs no action; "Escalator not
                        recorded" is a contract that will never be proposed for a rise until
                        someone sets its rate, and the two must not look the same.
                      */
                      <span className="text-xs text-ele-body">
                        {r.noEscalatorReason ? NO_ESCALATOR_LABEL[r.noEscalatorReason] : "No CPI figure"}
                      </span>
                    ) : (
                      <>
                        {gbp(r.proposedAnnual)}
                        {r.indicative && <span className="ml-1 text-[10px] uppercase text-ele-blue">(indicative)</span>}
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top max-md:hidden">
                    <CustomerLinkText link={r.customerLink} contractId={r.id} />
                  </td>
                  <td className="cell-actions px-3 py-2 text-right align-top max-md:hidden">
                    <div className="flex flex-col items-end gap-1">
                      <Link to={`/office/contracts/${r.id}?tab=renewal`} className="font-menu text-xs font-semibold tracking-wide text-ele-green hover:underline">
                        Start renewal
                      </Link>
                      <button onClick={() => setRenewFor(r)} title="A plain price rise letter with no customer link" className="text-xs text-ele-body opacity-80 hover:underline">
                        Draft renewal letter
                      </button>
                    </div>
                  </td>
                  <td className="cell-actions md:hidden pr-1 text-right">
                    <svg aria-hidden viewBox="0 0 16 16" className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green">
                      <path d="M6 4l4 4-4 4z" />
                    </svg>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {renewFor && <RenewalModal contract={renewFor} onClose={() => setRenewFor(null)} />}
    </div>
  );
}

// ---- Session AMW: the renewal-cycle fail-safe ------------------------------------------------
//
// Every renewal list on the platform, this page's own table included, is a window: an anchor within
// the next 90 days (and, since this session, an elapsed one). Two things could quietly take a
// contract OUT of every window without its review ever happening: a renewal date typed past an
// elapsed anniversary, and the Session AIP backfill run late (11 The Avenue: first review due 4 July
// 2026, backfill applied 13 August wrote 4 July 2027, no proposal ever minted, price still the
// migrated 250, nothing anywhere said so). This panel lists every active contract whose most recent
// anniversary, counted from its REAL first-term end and never the renewal date field, has no recorded
// action against it, or was settled without the date moving on. Derived live on every load (RULE 6),
// through the same function the dashboard tile, the contract page and the assistant's tools read.
const CYCLE_STATE_LABEL: Record<RenewalCycleState, string> = {
  not_active: "Not checked",
  no_dates: "No dates to check from",
  not_yet_due: "Nothing due yet",
  actioned: "Actioned",
  pending_proposal: "Proposal waiting for confirmation",
  missed_rolled_past: "Review missed, and the renewal date has moved past it",
  missed_overdue: "Review overdue",
  anchor_stalled: "Settled, but the renewal date has not moved on",
  // Session APA. "No record" rather than "not told", because the same state also covers a mailbox that
  // could not be checked just now; the message beneath says which it is. Session APB: what must be on
  // record is the NEW CHARGE (an email stating both figures), which an invitation alone never is.
  priced_not_confirmed_with_customer: "Price moved, no record the new charge was sent",
  unavailable: "Could not be checked",
};

function RenewalCyclesPanel({ cycles }: { cycles: RenewalCycleFinding[] }) {
  return (
    <section role="alert" className="mb-6 rounded-ele border border-ele-danger bg-ele-surface p-4">
      <h2 className="font-heading text-lg tracking-wide text-ele-danger">Renewal cycles needing attention</h2>
      <p className="mt-1 text-xs text-ele-body">
        These contracts have a renewal anniversary that has already passed without the review being actioned, a
        renewal date that never moved on after it was, or a CPI rise that was applied (moving the price and the
        renewal date) without the customer being sent the new charge. They do not appear in any forward-looking list, which is why
        they are listed here. Checked live from each contract&apos;s first term end, the proposals ledger and the
        office mailbox, never from the renewal date field or the notes.
      </p>
      <ul className="mt-3 space-y-2">
        {cycles.map((c) => (
          <li key={c.contractId} className="rounded-ele border border-ele-purple-accent px-3 py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="min-w-0 truncate text-sm text-ele-strong">
                {c.customerName} <span className="text-ele-body">/ {c.siteName}</span>
              </p>
              <span className="shrink-0 font-menu text-xs uppercase tracking-wide text-ele-danger">{CYCLE_STATE_LABEL[c.state] ?? c.state}</span>
            </div>
            <p className="mt-1 text-xs text-ele-body">{c.message}</p>
            <p className="mt-1 text-xs">
              <Link to={`/office/contracts/${c.contractId}?tab=renewal`} className="text-ele-blue hover:underline">
                Open renewal
              </Link>
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

// Session ARY, Item 4: contracts whose Xero repeating invoice no longer matches the price or cadence
// on the platform. Read-only here, deliberately -- there is no "update the template" action anywhere
// on the platform (Xero's API cannot update a repeating invoice), so the office corrects the amount
// in Xero itself and the flag clears at the next check; this panel just points at the record.
function XeroBillingOutOfStepPanel({ rows }: { rows: XeroBillingActionRow[] }) {
  return (
    <section className="mb-6 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <h2 className="font-heading text-lg tracking-wide text-ele-strong">Xero billing out of step</h2>
      <p className="mt-1 text-xs text-ele-body">
        Contracts whose Xero billing needs a person: an amount or schedule that no longer matches the contract, a repeating
        invoice deleted, paused or still billing an agreement that has ended, a check that could not read Xero, or a recent
        price change with no repeating invoice linked. Put it right in Xero, or link the template on the contract page; the
        flag clears at the next check.
      </p>
      <ul className="mt-3 space-y-2">
        {rows.map((r) => (
          <li key={r.contractId} className="rounded-ele border border-ele-purple-accent px-3 py-2">
            <p className="min-w-0 truncate text-sm text-ele-strong">
              {r.customerName} <span className="text-ele-body">/ {r.siteName}</span>
            </p>
            <p className="mt-1 text-xs text-ele-strong">{r.headline}</p>
            <p className="mt-0.5 text-xs text-ele-body">{r.detail}</p>
            <p className="mt-1 text-xs">
              <Link to={r.link} className="text-ele-blue hover:underline">
                Open contract
              </Link>
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---- Director decision D7 (Session AIC): propose, then confirm ------------------------------
//
// The daily sweep proposes a renewal (the new price and the drafted letter) into a ledger and stops.
// THIS PANEL IS THE CONFIRM STEP the decision asks for. Confirming applies the new annual value to
// the contract, which is what makes the recurring invoice bill the escalated rate, and creates a
// Gmail DRAFT of the letter. Nothing is sent to the customer and nothing is authorised in Xero:
// a person still presses send in Gmail, and the invoice is still a DRAFT confirmed on its own page.
//
// The confirm is the platform's two-step ConfirmButton and NAMES BOTH FIGURES, per the branded
// confirm rule for consequential actions: this applies a real price rise to a live contract.
function RenewalProposalsPanel() {
  const [rows, setRows] = useState<RenewalProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    api.contracts
      .renewalProposals()
      .then(setRows)
      .catch(() => setFailure("Could not load the proposed renewals."))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const act = async (id: string, fn: () => Promise<unknown>, done: string) => {
    setBusyId(id);
    setNotice(null);
    setFailure(null);
    try {
      await fn();
      setNotice(done);
      load();
    } catch (e) {
      // The confirm route answers 409 with a message that says whether the PRICE was applied even
      // when the letter failed. Surfacing that verbatim is the point: "it failed" alone would leave
      // the office not knowing whether the customer is now billed the new figure.
      setFailure(e instanceof Error ? e.message : "That did not work. Please try again.");
      load();
    } finally {
      setBusyId(null);
    }
  };

  const ready = rows.filter((r) => r.status === "ready");
  const other = rows.filter((r) => r.status !== "ready");
  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <section className="mb-6 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <h2 className="font-heading text-lg tracking-wide text-ele-strong">Proposed renewals</h2>
      <p className="mt-1 text-xs text-ele-body">
        Prepared automatically and held for your confirmation. Confirming applies the new annual value to the existing
        agreement and saves a plain price rise letter to Gmail drafts, with no customer facing link. Nothing is sent to
        the customer until you send it. If the customer needs to choose their own cover level and confirm online
        instead, use the renewal link from the contract's own page, not Confirm here.
      </p>

      <button
        onClick={() => void act("*", () => api.contracts.proposeRenewals(), "Checked for renewals due.")}
        className="mt-2 font-menu text-xs tracking-wide text-ele-green hover:underline max-sm:min-h-11"
      >
        Check for renewals due
      </button>

      {notice && <p className="mt-3 rounded-ele border border-ele-green px-3 py-2 text-xs text-ele-green">{notice}</p>}
      {failure && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger px-3 py-2 text-xs text-ele-danger">
          {failure}
        </p>
      )}

      <ul className="mt-3 space-y-2">
        {ready.map((p) => (
          <li key={p.id} className="rounded-ele border border-ele-purple-accent px-3 py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm text-ele-strong">
                  {p.customerName} <span className="text-ele-body">/ {p.siteName}</span>
                </p>
                <p className="mt-0.5 text-xs text-ele-body">
                  {gbp(p.currentAnnual)} to <span className="text-ele-strong">{gbp(p.proposedAnnual)}</span> a year, from{" "}
                  {formatDate(p.renewalDate)}. CPI {Number((p.cpiRate * 100).toFixed(2))} percent plus {p.escalatorPct} percent.
                </p>
                {/*
                  Session AMB, Director-reported confusion: Confirm here was pressed expecting the
                  interactive customer renewal page and instead produced this plain letter, with no cue
                  on screen beforehand that this was ever going to happen. This sentence is the fix:
                  said plainly, before the button, every time.
                */}
                <p className="mt-1 text-xs text-ele-body">
                  Confirm raises the price on the existing agreement and drafts a plain letter, no customer link. For
                  the interactive renewal page instead, use{" "}
                  <Link to={`/office/contracts/${p.contractId}?tab=renewal`} className="text-ele-blue hover:underline">
                    this contract&apos;s own page
                  </Link>
                  .
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <ConfirmButton
                  label="Confirm"
                  confirmLabel={`Press again: raise to ${gbp(p.proposedAnnual)}`}
                  busy={busyId === p.id}
                  busyLabel="Confirming..."
                  className="rounded-ele border border-ele-green px-3 py-1 font-menu text-xs tracking-wide text-ele-green transition hover:bg-ele-inset max-sm:min-h-11"
                  onConfirm={() =>
                    void act(p.id, () => api.contracts.confirmRenewal(p.id), `Price applied and the letter saved to Gmail drafts for ${p.siteName}.`)
                  }
                />
                <ConfirmButton
                  label="Remove"
                  confirmLabel="Press again to remove"
                  busy={busyId === p.id}
                  busyLabel="Removing..."
                  className="rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:bg-ele-inset max-sm:min-h-11"
                  onConfirm={() => void act(p.id, () => api.contracts.skipRenewalProposal(p.id), `Removed the proposal for ${p.siteName}.`)}
                />
              </div>
            </div>
            {/*
              The office must be able to READ what it is confirming. The letter was carried in the
              payload and rendered nowhere, so Confirm drafted a customer letter nobody had seen.
            */}
            <details className="mt-2">
              <summary className="cursor-pointer font-menu text-xs tracking-wide text-ele-blue">Read the letter</summary>
              <p className="mt-1 whitespace-pre-wrap text-xs text-ele-body">{p.letterBody}</p>
            </details>
          </li>
        ))}
      </ul>

      {other.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-ele-purple-accent pt-3">
          {other.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 text-xs text-ele-body">
              <span className="truncate">
                {p.siteName}: {PROPOSAL_STATUS_LABEL[p.status]}
                {p.status === "confirmed" && ` at ${gbp(p.proposedAnnual)}`}
                {p.error ? ` (${p.error})` : ""}
              </span>
              <span className="flex shrink-0 items-center gap-3">
                {p.status === "skipped" && (
                  <button
                    onClick={() => void act(p.id, () => api.contracts.restoreRenewalProposal(p.id), `Restored the proposal for ${p.siteName}.`)}
                    className="text-ele-green hover:underline max-sm:min-h-11"
                  >
                    Restore
                  </button>
                )}
                {/*
                  RULE 1. Only offered while the price has NOT been applied: once it has, the row is
                  the record of a real change and the route 409s. `skip` remains the reversible path.
                */}
                {p.appliedAt == null && p.status !== "confirmed" && (
                  <ConfirmButton
                    label="Delete"
                    confirmLabel="Press again to delete"
                    busy={busyId === p.id}
                    className="font-menu text-xs tracking-wide text-ele-body hover:underline max-sm:min-h-11"
                    onConfirm={() => void act(p.id, () => api.contracts.deleteRenewalProposal(p.id), `Deleted the proposal for ${p.siteName}.`)}
                  />
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
